import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  if (import.meta.env.DEV) {
    console.error(
      '[supabase] Missing env vars — VITE_SUPABASE_URL:',
      supabaseUrl ? 'set' : 'MISSING',
      '| VITE_SUPABASE_ANON_KEY:',
      supabaseAnonKey ? 'set' : 'MISSING'
    );
  }
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true
  },
  global: {
    headers: {}
  }
});

/**
 * Set the active team for RLS scoping.
 * All subsequent PostgREST queries will include this header,
 * and current_user_team_id() will honor it (after validating membership).
 */
export const setActiveTeamId = (teamId) => {
  if (teamId) {
    supabase.rest.headers['x-active-team-id'] = teamId;
  } else {
    delete supabase.rest.headers['x-active-team-id'];
  }
};


const getProjectRefFromUrl = (url) => {
  try {
    const hostname = new URL(url)?.hostname || '';
    return hostname?.split('.')?.[0] || 'unknown';
  } catch {
    return 'unknown';
  }
};

const testTableAccess = async (table) => {
  const { count, error } = await supabase
    ?.from(table)
    ?.select('*', { count: 'exact', head: true });

  return {
    table,
    ok: !error,
    count: typeof count === 'number' ? count : null,
    error: error?.message || null,
    code: error?.code || null,
  };
};

// Refresh connection helper - lightweight session check instead of
// 4 table-scan queries (was: user_profiles, materials, products, team_members).
export const refreshSupabaseConnection = async () => {
  try {
    const projectRef = getProjectRefFromUrl(supabaseUrl);

    // Single lightweight check instead of 4 COUNT(*) queries
    const check = await testTableAccess('user_profiles');

    if (!check.ok) {
      return {
        success: false,
        error: check.error || 'Database connection check failed',
        details: { projectRef, checks: [check] },
      };
    }
    
    return {
      success: true,
      message: 'Connection refreshed successfully',
      details: { projectRef, checks: [check] },
    };
  } catch (error) {
    return {
      success: false,
      error: error?.message,
      details: {
        projectRef: getProjectRefFromUrl(supabaseUrl),
      },
    };
  }
};

// Test connection helper
export const testSupabaseConnection = async () => {
  try {
    const { data, error } = await supabase?.from('user_profiles')?.select('count', { count: 'exact', head: true });
    if (error) throw error;
    return { success: true, message: 'Connected to Supabase' };
  } catch (error) {
    return { success: false, error: error?.message };
  }
};

// Get config info (safe — no secrets exposed)
export const getSupabaseConfig = () => ({
  url: supabaseUrl,
  hasAnonKey: !!supabaseAnonKey,
  projectRef: getProjectRefFromUrl(supabaseUrl),
});
